/**
 * Builds the Excel/Word/PDF export for the Preferred Creditors register.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered register:
 * one row per active employee, matching the table columns.
 *
 * The FEG verification spreadsheet is a separate download (`FegExportButton`)
 * and is not produced here.
 *
 * Money is written as a real number and dates as real Date cells so the
 * practitioner can sort / sum in Excel.
 */

import {
  EMPLOYMENT_BASIS_LABELS,
  sumEmployeeEntitlements,
} from "@cvx/lib/employeeEntitlements";
import type { ExportContent, ExportSheet } from "@/utils/export";
import type { EmployeeEntryUI } from "./types";

function money(value?: number): number {
  return value ?? 0;
}

function asDate(raw?: string): Date | "" {
  if (!raw) return "";
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : parsed;
}

function basisLabel(entry: EmployeeEntryUI): string {
  const key = entry.employmentCategory;
  if (!key) return "";
  return EMPLOYMENT_BASIS_LABELS[key] ?? key;
}

function preferredCreditorsSheet(entries: EmployeeEntryUI[]): ExportSheet {
  const sorted = [...entries]
    .filter((e) => e.status === "active" && !e.isExcluded)
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    name: "Preferred Creditors",
    headers: [
      "Name",
      "Role",
      "Related party",
      "Basis",
      "Start",
      "End",
      "Wages",
      "Super",
      "Ann. lv",
      "Lv loading",
      "LSL",
      "PILON",
      "Total",
    ],
    rows: sorted.map((entry) => [
      entry.name,
      entry.role ?? "",
      entry.isRelatedParty ? "Yes" : "",
      basisLabel(entry),
      asDate(entry.startDate),
      asDate(entry.terminationDate),
      money(entry.entitlements?.unpaidWages),
      money(entry.entitlements?.unpaidSuper),
      money(entry.entitlements?.accruedAnnualLeave),
      money(entry.entitlements?.leaveLoading),
      money(entry.entitlements?.accruedLongServiceLeave),
      money(entry.entitlements?.paymentInLieuOfNotice),
      money(sumEmployeeEntitlements(entry.entitlements)),
    ]),
  };
}

export function buildPreferredCreditorsExport(
  entries: EmployeeEntryUI[],
): ExportContent {
  return {
    title: "Preferred Creditors",
    sheets: [preferredCreditorsSheet(entries)],
  };
}
